import type { GQL_ID } from '@@partner:types:graphql'
import type {
  ProductBrand,
  ProductEan,
  ProductTranslation,
} from '@@partner:types:app'
import type { ProductFormGalleryItem, ProductFormState } from '../types'

import React, { useCallback, useState, forwardRef } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { ReactSortable } from 'react-sortablejs'

import { Col, Form, Grid, Row, Checkbox, IconButton, Tag } from 'rsuite'
import CloseIcon from '@rsuite/icons/Close'
import ProductBrandsSelector from '../form/ProductBrandsSelector'
import ProductStatus from '../form/info/ProductStatus'
import PriceType from '../form/price/PriceType'
import ProductCategoriesManager from '../form/categories/ProductCategoriesManager'
import FormTextareaControl from '@components/common/form/FormTextareaControl'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import ProductEansManager from '../form/eans/ProductEansManager'
import ProductMarkingManager from '../form/marking/ProductMarkingManager'
import LabelsManager from '../form/labels/LabelsManager'
import LabelsSelectedItems from '../form/labels/LabelsSelectedItems'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import FormCheckbox from '@components/common/form/FormCheckbox'
import AttributesTable from '../form/attributes/AttributesManager'
import SavablePanel from '../form/SavablePanel'
import { ReadField, ReadBoolField, ReadRow } from '../form/ReadField'
import { Link, generatePath } from 'react-router-dom'
import { ROUTE_CATALOG_CATEGORY_EDIT } from '@@partner:config/routes'

import { API_URL_UPLOAD_PRODUCT_PREVIEW } from '@@partner:config/api'
import { UPLOAD_NAME_PRODUCT_PREVIEW } from '@@partner:config/product'
import { CUSTOMER_FRONTEND_URL } from '@@partner:config'
import { buildProduct } from '@hive/platform-url-schema'
import {
  makeProductFormState,
  PRODUCT_TRANSLATION_DEFAULT_STATE
} from '../ProductFormState'
import type { ProductAttribute } from '@@partner:types:app'

const CustomComponent = forwardRef<HTMLDivElement, any>((props, ref) => {
  return <div ref={ref} style={{ flexDirection: 'row' }}>{props.children}</div>
})

export interface ProductInfoTabProps {
  formState: ProductFormState
  setFormState: React.Dispatch<React.SetStateAction<ProductFormState>>
  formError: Record<string, any>
  loadingProduct: boolean
  loadingSave: boolean
  attributes: ProductAttribute[]
  brandSlug: string
  onSave: () => void
}

export default function ProductInfoTab ({
  formState,
  setFormState,
  formError,
  loadingProduct,
  loadingSave,
  attributes,
  brandSlug,
  onSave
}: ProductInfoTabProps) {
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>()

  const handleProductBrandChange = useCallback(
    (brand?: ProductBrand) => {
      if (brand) {
        setFormState((state) => mergeLeft(state, { brand, brand_id: brand.id }))
      } else {
        setFormState((state) =>
          mergeLeftOnly([ 'brand', 'brand_id' ], state, makeProductFormState())
        )
      }
    },
    [ setFormState ]
  )

  const handlePriceTypeChange = useCallback(
    (price_type: 'piece' | 'weight') => {
      setFormState((state) =>
        mergeLeftOnly([ 'price_type', 'count_multiplier', 'min_count' ], state, {
          count_multiplier: price_type === 'piece' ? 1 : 100,
          min_count: price_type === 'piece' ? 1 : 100,
          price_type
        })
      )
    },
    [ setFormState ]
  )

  const handleStatusChange = useCallback(
    (status) => {
      setFormState((state) => mergeLeftOnly([ 'status' ], state, { status }))
    },
    [ setFormState ]
  )

  const handleCategoriesChange = (categoryIds: GQL_ID[]) => {
    setFormState((state) =>
      mergeLeftOnly([ 'categoryIds' ], state, { categoryIds })
    )
  }

  const handleLabelsChange = (marketingLabelIds: GQL_ID[]) => {
    setFormState((state) =>
      mergeLeftOnly([ 'marketingLabelIds' ], state, { marketingLabelIds })
    )
  }

  const handleEansChange = (eans: ProductEan[] = []) => {
    setFormState((state) => mergeLeftOnly([ 'eans' ], state, { eans }))
  }

  const handleMarkableChange = useCallback(
    ({ markable, markCodeType }) => {
      setFormState((state) =>
        mergeLeftOnly([ 'markable', 'markCodeType' ], state, { markable, markCodeType })
      )
    },
    [ setFormState ]
  )

  const handleProductPreviewUploaded = useCallback(
    (preview_id, url) => {
      setFormState((state) =>
        mergeLeftOnly([ 'preview_id', 'preview', 'url' ], state, {
          preview_id,
          preview: { url }
        })
      )
    },
    [ setFormState ]
  )

  const handleProductImageGalleryUploaded = useCallback(
    (preview_id, url) => {
      setFormState((state) =>
        mergeLeftOnly([ 'galleryImages' ], state, {
          galleryImages: [
            ...state.galleryImages,
            {
              id: preview_id,
              url,
              weight: state.galleryImages && state.galleryImages.length > 0
                ? Math.max(...state.galleryImages.map(image => image.weight)) + 1
                : 5
            }
          ]
        })
      )
    },
    [ setFormState ]
  )

  const handleProductImagesOrderChanged = useCallback(
    (newState: ProductFormGalleryItem[]) => {
      setFormState((state) =>
        mergeLeftOnly([ 'galleryImages' ], state, {
          galleryImages: newState.map((image, index) => ({
            id: image.id,
            url: image.url,
            weight: 5 + index
          }))
        })
      )
    },
    [ setFormState ]
  )

  const handleDeleteGalleryImage = useCallback(
    (imageId: GQL_ID) => {
      setFormState((state) =>
        mergeLeftOnly([ 'galleryImages' ], state, {
          galleryImages: state.galleryImages.filter(image => image.id !== imageId)
        })
      )
    },
    [ setFormState ]
  )

  const handleUnlinkPreviewClick = useCallback(() => {
    setFormState((state) =>
      mergeLeftOnly(
        [ 'preview_id', 'preview', 'url' ],
        state,
        makeProductFormState()
      )
    )
  }, [ setFormState ])

  const handleTranslateChange = useCallback(
    (field: keyof ProductTranslation) => (value: string) => {
      const newTranslations = [ ...formState.i18n ]
      const translationIndex = newTranslations.findIndex(
        translation => translation.language_code === selectedLanguage
      )
      if (translationIndex > -1) {
        newTranslations[translationIndex][field] = value
      } else {
        const newTranslation = mergeLeft(PRODUCT_TRANSLATION_DEFAULT_STATE, {
          language_code: selectedLanguage,
          [field]: value
        })
        newTranslations.push(newTranslation)
      }
      setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
    },
    [ formState.i18n, selectedLanguage, setFormState ]
  )

  const previewUrl = formState.preview?.url ? formState.preview?.url : null

  const mainDataReadView = (
    <div>
      <ReadRow>
        <ReadField label="Название" value={formState.name} xs={24} md={24} />
      </ReadRow>
      <ReadRow>
        <ReadField label="Описание" value={formState.short_description} xs={24} md={24} />
      </ReadRow>
      <ReadRow>
        <ReadField label="ID в учетной системе" value={formState.xid} />
        <ReadField label="Артикул" value={formState.article_num} />
      </ReadRow>
      <ReadRow>
        <ReadField
          label="Имя для URL"
          value={formState.slug}
          xs={24}
          md={24}
        />
      </ReadRow>
      <ReadRow>
        <ReadField label="Тип товара" value={formState.price_type === 'piece' ? 'Штучный' : 'Весовой'} />
        <ReadField label="Бренд" value={formState.brand?.name} />
      </ReadRow>
      <ReadRow>
        <ReadField label="Мін. шаг в корзину" value={formState.min_count} />
        <ReadField label="Макс. в корзине" value={formState.max_amount} />
      </ReadRow>
      {(previewUrl || formState.galleryImages.length > 0) && (
        <div className="mt-3">
          <div className="text-gray-500 text-xs mb-1">Изображения</div>
          <div className="flex flex-wrap gap-2 items-start">
            {previewUrl && (
              <img
                className="w-24 h-24 object-cover rounded-lg border-2 border-blue-300 shadow-md"
                src={previewUrl}
                alt="Превью"
              />
            )}
            {formState.galleryImages.map((item) => (
              <img
                key={item.id}
                className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                src={item.url}
                alt="Галерея"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )

  const classificationReadView = (
    <div>
      {formState.categoryChains.length > 0 && (
        <div className="mb-3">
          <div className="text-gray-500 text-xs mb-1">Категории</div>
          <div className="flex flex-wrap gap-1">
            {formState.categoryChains.map((chain) => (
              <Link
                key={chain.id}
                to={generatePath(ROUTE_CATALOG_CATEGORY_EDIT, { brandSlug, categoryId: chain.id })}
                style={{ textDecoration: 'none' }}
              >
                <Tag style={{ cursor: 'pointer' }}>{chain.namesChain.join(' → ')}</Tag>
              </Link>
            ))}
          </div>
        </div>
      )}
      <ReadRow>
        <ReadField label="Приоритет в списках" value={formState.priority} />
      </ReadRow>
      <div className="flex flex-wrap mt-2">
        <ReadBoolField label="Собственное производство" value={formState.is_private_label} />
        <ReadBoolField label="СТМ" value={formState.is_own_brand} />
      </div>
      {formState.marketingLabelIds.length > 0 && (
        <div className="mt-3">
          <div className="text-gray-500 text-xs mb-1">Лейблы</div>
          <LabelsSelectedItems
            items={formState.marketingLabelIds}
            loading={loadingProduct}
            onCategoryDrop={() => {}}
          />
        </div>
      )}
    </div>
  )

  const dimensionsReadView = (
    <div>
      <ReadRow>
        <ReadField label="Вес брутто" value={formState.weight} suffix="г" xs={12} md={8} />
        <ReadField label="Вес нетто" value={formState.weight_netto} suffix="г" xs={12} md={8} />
        <ReadField label="Длина" value={formState.l} xs={12} md={8} />
        <ReadField label="Ширина" value={formState.w} xs={12} md={8} />
        <ReadField label="Высота" value={formState.h} xs={12} md={8} />
        <ReadField label="Объём" value={formState.volume} xs={12} md={8} />
      </ReadRow>
    </div>
  )

  const additionalInfoReadView = (
    <div>
      <ReadRow>
        <ReadField label="Калории" value={formState.calories} sm={6} xs={12} md={6} />
        <ReadField label="Белки" value={formState.proteins} sm={6} xs={12} md={6} />
        <ReadField label="Жиры" value={formState.fats} sm={6} xs={12} md={6} />
        <ReadField label="Углеводы" value={formState.carbohydrates} sm={6} xs={12} md={6} />
      </ReadRow>
      <ReadRow>
        <ReadField label="Упаковка" value={formState.pkg} xs={12} md={12} />
        <ReadField label="Производитель" value={formState.manufacturer} xs={12} md={12} />
      </ReadRow>
      <ReadRow>
        <ReadField label="Ингредиенты" value={formState.ingredients} xs={24} md={24} />
      </ReadRow>
    </div>
  )

  const accountingReadView = (
    <div>
      <div className="flex flex-wrap">
        <ReadBoolField label="Алкоголь" value={formState.is_alcohol} />
        <ReadBoolField label="Подакцизный" value={formState.excisable} />
        <ReadBoolField label="Маркировка" value={formState.markable} />
      </div>
      {formState.eans?.length > 0 && (
        <div className="mt-3">
          <div className="text-gray-500 text-xs mb-1">Штрих-коды</div>
          <div className="flex flex-wrap gap-1">
            {formState.eans.map((ean, i) => (
              <Tag key={i}>{ean.ean}</Tag>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  const localizationReadView = (
    <div>
      {formState.languages.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {formState.languages.map((lang) => (
            <Tag key={lang} color="blue">{lang}</Tag>
          ))}
        </div>
      ) : (
        <div className="text-gray-400 text-sm">Локализации не добавлены</div>
      )}
    </div>
  )

  return (
    <div>
      <Grid fluid>
        <Col xs={24} sm={24} md={14}>
          {/* Основные данные */}
          <SavablePanel
            header="Основные данные о товаре"
            loading={loadingProduct}
            saving={loadingSave}
            onSave={onSave}
            readView={mainDataReadView}
          >
            <Row className="my-5">
              <Col xs={24}>
                <Form.Group>
                  <Form.ControlLabel>Название</Form.ControlLabel>
                  <Form.Control placeholder="Название" name="name" />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-5">
              <Col xs={24}>
                <Form.Group>
                  <Form.ControlLabel>Описание</Form.ControlLabel>
                  <FormTextareaControl
                    placeholder="Описание"
                    name="short_description"
                    rows={5}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-5">
              <Col xs={12} md={12}>
                <Form.Group>
                  <Form.ControlLabel>ID в учетной системе</Form.ControlLabel>
                  <Form.Control placeholder="ID в учетной системе" name="xid" />
                </Form.Group>
              </Col>
              <Col xs={12} md={12}>
                <Form.Group>
                  <Form.ControlLabel>Артикул</Form.ControlLabel>
                  <Form.Control placeholder="Артикул" name="article_num" />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-5">
              <Col xs={24}>
                <Form.Group>
                  <Form.ControlLabel>
                    Имя для URL{' '}
                    <a href={`${CUSTOMER_FRONTEND_URL}${buildProduct(formState.slug || '')}`}>
                      (Перейти)
                    </a>
                  </Form.ControlLabel>
                  <Form.Control placeholder="Имя для URL" name="slug" />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-5">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Статус</Form.ControlLabel>
                  <ProductStatus
                    value={formState.status}
                    onChange={handleStatusChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Виден покупателям</Form.ControlLabel>
                  <FormCheckbox.Control name="indexable" />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Тип товара</Form.ControlLabel>
                  <PriceType
                    value={formState.price_type}
                    onChange={handlePriceTypeChange}
                  />
                  {formError.price_type && (
                    <Form.ErrorMessage show={true}>
                      {formError.price_type}
                    </Form.ErrorMessage>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Бренд</Form.ControlLabel>
                  <ProductBrandsSelector
                    onChange={handleProductBrandChange}
                    value={formState.brand}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-5">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Мин. шаг в корзину</Form.ControlLabel>
                  <Form.Control
                    placeholder="Минимальный шаг"
                    name="min_count"
                    type="number"
                    min={1}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Макс. в корзине</Form.ControlLabel>
                  <Form.Control
                    placeholder="Максимум в корзине"
                    name="max_amount"
                    type="number"
                    min={0}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-5">
              <Col xs={24} md={12}>
                <Form.Group>
                  <Form.ControlLabel>Изображение товара (jpg, png)</Form.ControlLabel>
                  {previewUrl ? (
                    <DropzonePreview
                      url={previewUrl}
                      onDelete={handleUnlinkPreviewClick}
                    />
                  ) : (
                    <DropzoneUploader
                      name={UPLOAD_NAME_PRODUCT_PREVIEW}
                      action={API_URL_UPLOAD_PRODUCT_PREVIEW}
                      onUploaded={handleProductPreviewUploaded}
                      multiple={false}
                    />
                  )}
                </Form.Group>
              </Col>
              <Col xs={24} md={12}>
                <Form.Group>
                  <Form.ControlLabel>Галерея</Form.ControlLabel>
                  <ReactSortable
                    direction="horizontal"
                    tag={CustomComponent}
                    list={formState.galleryImages}
                    setList={handleProductImagesOrderChanged}
                  >
                    {formState.galleryImages.map((item) => (
                      <div
                        key={`image:${item.id}`}
                        className="relative inline-block mr-2 mb-2 group cursor-move"
                        style={{ position: 'relative' }}
                      >
                        <img
                          className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 shadow-md hover:shadow-lg transition-all duration-200 hover:border-blue-300"
                          style={{ display: 'block' }}
                          src={item.url}
                          alt="Gallery item"
                        />
                        <IconButton
                          icon={<CloseIcon />}
                          size="xs"
                          appearance="subtle"
                          color="blue"
                          circle
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(59, 130, 246, 0.8)',
                            backdropFilter: 'blur(4px)',
                            zIndex: 10,
                            border: '2px solid rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                            color: 'white'
                          }}
                          onClick={() => handleDeleteGalleryImage(item.id)}
                          title="Удалить изображение"
                        />
                      </div>
                    ))}
                  </ReactSortable>
                  <DropzoneUploader
                    name={UPLOAD_NAME_PRODUCT_PREVIEW}
                    action={API_URL_UPLOAD_PRODUCT_PREVIEW}
                    onUploaded={handleProductImageGalleryUploaded}
                    multiple={false}
                  />
                </Form.Group>
              </Col>
            </Row>
          </SavablePanel>

          {/* Классификация */}
          <SavablePanel
            header="Классификация"
            loading={loadingProduct}
            saving={loadingSave}
            onSave={onSave}
            readView={classificationReadView}
          >
            <Row>
              <Col xs={24} md={18}>
                <Form.Group>
                  <Form.ControlLabel>Категории</Form.ControlLabel>
                  <ProductCategoriesManager
                    value={formState.categoryIds}
                    onChange={handleCategoriesChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={24} md={6}>
                <Form.Group>
                  <Form.ControlLabel>Приоритет в списках</Form.ControlLabel>
                  <Form.Control
                    placeholder="Приоритет в списках"
                    name="priority"
                    type="number"
                    min={0}
                    value={formState.priority}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Checkbox
                checked={formState.is_private_label}
                onChange={() =>
                  setFormState((state) => ({
                    ...state,
                    is_private_label: !state.is_private_label
                  }))
                }
              >
                Собственное производство
              </Checkbox>
              <Checkbox
                checked={formState.is_own_brand}
                onChange={() =>
                  setFormState((state) => ({
                    ...state,
                    is_own_brand: !state.is_own_brand
                  }))
                }
              >
                СТМ
              </Checkbox>
            </Row>
            <Row className="mt-3">
              <Col xs={24}>
                <Form.Group>
                  <Form.ControlLabel>Лейблы</Form.ControlLabel>
                  <LabelsManager
                    value={formState.marketingLabelIds}
                    loading={loadingProduct}
                    onChange={handleLabelsChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </SavablePanel>

          {/* Дополнительная информация */}
          <SavablePanel
            header="Дополнительная информация"
            loading={loadingProduct}
            saving={loadingSave}
            onSave={onSave}
            readView={additionalInfoReadView}
          >
            <Row>
              <Col sm={6}>
                <Form.Group>
                  <Form.ControlLabel>Калории</Form.ControlLabel>
                  <Form.Control placeholder="Калории" name="calories" />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.ControlLabel>Белки</Form.ControlLabel>
                  <Form.Control placeholder="Белки" name="proteins" />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.ControlLabel>Жиры</Form.ControlLabel>
                  <Form.Control placeholder="Жиры" name="fats" />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.ControlLabel>Углеводы</Form.ControlLabel>
                  <Form.Control placeholder="Углеводы" name="carbohydrates" />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>Упаковка</Form.ControlLabel>
                  <Form.Control placeholder="Упаковка" name="pkg" />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Ингредиенты</Form.ControlLabel>
                  <FormTextareaControl
                    placeholder="Ингредиенты"
                    name="ingredients"
                    rows={5}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>Производитель</Form.ControlLabel>
                  <Form.Control placeholder="Производитель" name="manufacturer" />
                </Form.Group>
              </Col>
            </Row>
          </SavablePanel>

          {/* Локализация */}
          <SavablePanel
            header="Локализация"
            loading={loadingProduct}
            saving={loadingSave}
            onSave={onSave}
            readView={localizationReadView}
          >
            <Row>
              <SelectLocale
                selectedLanguage={selectedLanguage}
                onSelect={(language) => setSelectedLanguage(language)}
                onAdd={(language) => {
                  setFormState((state) =>
                    mergeLeft(state, { languages: [ ...state.languages, language ] })
                  )
                }}
                languages={formState.languages}
              />
              {selectedLanguage && (
                <div className="my-5">
                  <Col xs={24}>
                    <LocalizedForm
                      name="name"
                      label="Название"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.name || ''
                      }
                      onChange={handleTranslateChange('name')}
                    />
                    <LocalizedForm
                      name="short_description"
                      label="Краткое описание"
                      type="textarea"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.short_description || ''
                      }
                      onChange={handleTranslateChange('short_description')}
                    />
                  </Col>
                  <Col xs={12} className="my-2">
                    <LocalizedForm
                      name="ingredients"
                      label="Ингредиенты"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.ingredients || ''
                      }
                      onChange={handleTranslateChange('ingredients')}
                    />
                    <LocalizedForm
                      name="pkg"
                      label="Упаковка"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.pkg || ''
                      }
                      onChange={handleTranslateChange('pkg')}
                    />
                    <LocalizedForm
                      name="volume"
                      label="Объем"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.volume || ''
                      }
                      onChange={handleTranslateChange('volume')}
                    />
                  </Col>
                  <Col xs={12} className="my-2">
                    <LocalizedForm
                      name="manufacturer"
                      label="Производитель"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.manufacturer || ''
                      }
                      onChange={handleTranslateChange('manufacturer')}
                    />
                    <LocalizedForm
                      name="country"
                      label="Страна"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.country || ''
                      }
                      onChange={handleTranslateChange('country')}
                    />
                    <LocalizedForm
                      name="storage_conditions"
                      label="Условия хранения"
                      selectedLanguage={selectedLanguage}
                      value={
                        formState.i18n.find(
                          (t) => t.language_code === selectedLanguage
                        )?.storage_conditions || ''
                      }
                      onChange={handleTranslateChange('storage_conditions')}
                    />
                  </Col>
                </div>
              )}
            </Row>
          </SavablePanel>
        </Col>

        <Col xs={24} sm={24} md={10}>
          {/* Габариты */}
          <SavablePanel
            header="Габариты"
            loading={loadingProduct}
            saving={loadingSave}
            onSave={onSave}
            readView={dimensionsReadView}
          >
            <Row>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Вес брутто (грамм)</Form.ControlLabel>
                  <Form.Control placeholder="Граммы" name="weight" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Вес нетто (грамм)</Form.ControlLabel>
                  <Form.Control placeholder="Граммы" name="weight_netto" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Длина</Form.ControlLabel>
                  <Form.Control placeholder="Длина" name="l" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Ширина</Form.ControlLabel>
                  <Form.Control placeholder="Ширина" name="w" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Высота</Form.ControlLabel>
                  <Form.Control placeholder="Высота" name="h" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Объём</Form.ControlLabel>
                  <Form.Control placeholder="Объём" name="volume" />
                </Form.Group>
              </Col>
            </Row>
          </SavablePanel>

          {/* Учёт и маркировка */}
          <SavablePanel
            header="Учёт и маркировка"
            loading={loadingProduct}
            saving={loadingSave}
            onSave={onSave}
            readView={accountingReadView}
          >
            <Row>
              <Col xs={24}>
                <Form.Group>
                  <Form.ControlLabel>Штрих-коды</Form.ControlLabel>
                  <ProductEansManager
                    value={formState.eans}
                    onChange={handleEansChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={24}>
                <ProductMarkingManager
                  markable={formState.markable}
                  markCodeType={formState.markCodeType}
                  onChange={handleMarkableChange}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Checkbox
                checked={formState.is_alcohol}
                onChange={() =>
                  setFormState((state) => ({
                    ...state,
                    is_alcohol: !state.is_alcohol
                  }))
                }
              >
                Алкоголь
              </Checkbox>
              <Checkbox
                checked={formState.excisable}
                onChange={() =>
                  setFormState((state) => ({
                    ...state,
                    excisable: !state.excisable
                  }))
                }
              >
                Подакцизный
              </Checkbox>
            </Row>
          </SavablePanel>

          {/* Атрибуты */}
          <SavablePanel header="Атрибуты" loading={loadingProduct}>
            <AttributesTable
              attributes={attributes}
              loadingProduct={loadingProduct}
            />
          </SavablePanel>
        </Col>
      </Grid>
    </div>
  )
}
